/**
 * API Service for Audit Logs
 * Centralized API calls for audit log monitoring
 */

import api from "../../auth/apiClient";
import {
    PaginatedAuditLogs,
    DashboardAggregations,
    SuccessFailureRatio,
    TimeSeriesData,
    FailedUser,
    FailureReason,
    TopSource,
    AuditLogFilters,
    ExportFilters,
} from "./types";
import { AUDIT_LOGS_API_CONFIG } from "./apiConfig";
import { buildFilterParams, generateExportFilename } from "./utils";
import { trackDownload } from "../../activity";

class AuditLogsApiService {
    /**
     * Get paginated audit logs with filters
     */
    async getAuditLogs(filters: AuditLogFilters): Promise<PaginatedAuditLogs> {
        const params = buildFilterParams(filters);
        const response = await api.get<PaginatedAuditLogs>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.LIST,
            { params }
        );
        return response.data;
    }

    /**
     * Get dashboard aggregations data
     */
    async getDashboardAggregations(filters: AuditLogFilters): Promise<DashboardAggregations> {
        const params = buildFilterParams(filters);
        const response = await api.get<DashboardAggregations>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.DASHBOARD,
            { params }
        );
        return response.data;
    }

    /**
     * Get success/failure ratio chart data
     */
    async getSuccessFailureRatio(filters: AuditLogFilters): Promise<SuccessFailureRatio> {
        const params = buildFilterParams(filters);
        const response = await api.get<SuccessFailureRatio>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.CHARTS_SUCCESS_FAILURE,
            { params }
        );
        return response.data;
    }

    /**
     * Get time series chart data
     */
    async getTimeSeriesData(filters: AuditLogFilters): Promise<TimeSeriesData> {
        const params = buildFilterParams(filters);
        const response = await api.get<TimeSeriesData>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.CHARTS_TIME_SERIES,
            { params }
        );
        return response.data;
    }

    /**
     * Get top failed users for brute force detection
     */
    async getTopFailedUsers(
        filters: AuditLogFilters,
        limit: number = 10
    ): Promise<FailedUser[]> {
        const params = { ...buildFilterParams(filters), limit };
        const response = await api.get<FailedUser[]>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.TOP_FAILED_USERS,
            { params }
        );
        return response.data;
    }

    /**
     * Get top failure reasons
     */
    async getTopFailureReasons(filters: AuditLogFilters): Promise<FailureReason[]> {
        const params = buildFilterParams(filters);
        const response = await api.get<FailureReason[]>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.TOP_FAILURE_REASONS,
            { params }
        );
        return response.data;
    }

    /**
     * Get top sources
     */
    async getTopSources(filters: AuditLogFilters): Promise<TopSource[]> {
        const params = buildFilterParams(filters);
        const response = await api.get<TopSource[]>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.TOP_SOURCES,
            { params }
        );
        return response.data;
    }

    /**
     * Export audit logs to CSV or Excel
     */
    async exportAuditLogs(filters: ExportFilters): Promise<Blob> {
        const params = buildFilterParams(filters);
        const filename = filters.filename || generateExportFilename(
            filters.export_format,
            filters.time_range || "custom"
        );

        const response = await api.get<Blob>(
            AUDIT_LOGS_API_CONFIG.ENDPOINTS.EXPORT,
            {
                params: {
                    ...params,
                    export_format: filters.export_format,
                    filename,
                },
                responseType: "blob",
            }
        );

        return response.data;
    }

    /**
     * Download exported file
     */
    downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        trackDownload("Audit Log Login", { filename });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}

export default new AuditLogsApiService();
